/**
 * Пункты «Наблюдать» для меню столбца, доски и проекта.
 *
 * Не наблюдает — «Наблюдать за …» открывает выбор событий. Наблюдает —
 * «Вы наблюдатель · настроить» (тот же диалог с текущим набором)
 * и «Прекратить наблюдение» сразу из меню.
 */

import { WatchTargetType } from "@taskflow/contracts";
import { Bell, BellOff, Eye } from "lucide-react";
import { useState } from "react";

import { errorMessage } from "@taskflow/client-core";
import {
  WATCH_TARGET_LABELS,
  findWatch,
  useRemoveWatchMutation,
  useWatchListQuery,
} from "@taskflow/client-core/watching";
import { type MenuAction, toast } from "@/shared/ui";

import { WatchDialog, type WatchTargetInfo } from "./WatchDialog";

/** «Вы наблюдатель столбца». */
const GENITIVE: Record<WatchTargetType, string> = {
  [WatchTargetType.TASK]: "задачи",
  [WatchTargetType.COLUMN]: "столбца",
  [WatchTargetType.BOARD]: "доски",
  [WatchTargetType.PROJECT]: "проекта",
};

export function useWatchMenu() {
  const list = useWatchListQuery();
  const [removeWatch] = useRemoveWatchMutation();
  const [open, setOpen] = useState<WatchTargetInfo | null>(null);

  /** Пункты меню для объекта; separated — разделитель перед первым. */
  const actionsFor = (target: WatchTargetInfo, separated = false): MenuAction[] => {
    const current = findWatch(list.data, target.targetType, target.targetId);
    const what = WATCH_TARGET_LABELS[target.targetType];
    if (current === undefined) {
      return [
        {
          key: `watch-${target.targetType}`,
          label: `Наблюдать за ${what}`,
          icon: Eye,
          separated,
          disabled: list.isLoading,
          onSelect: () => setOpen(target),
        },
      ];
    }
    return [
      {
        key: `watching-${target.targetType}`,
        label: `Вы наблюдатель ${GENITIVE[target.targetType]} · настроить`,
        icon: Bell,
        separated,
        onSelect: () => setOpen(target),
      },
      {
        key: `unwatch-${target.targetType}`,
        label: "Прекратить наблюдение",
        icon: BellOff,
        onSelect: () =>
          void removeWatch({ targetType: target.targetType, targetId: target.targetId })
            .unwrap()
            .then(() => toast.success("Наблюдение прекращено", `«${target.name}»`))
            .catch((raw: unknown) => toast.error(errorMessage(raw))),
      },
    ];
  };

  const dialog =
    open === null ? null : (
      <WatchDialog
        target={open}
        current={findWatch(list.data, open.targetType, open.targetId)}
        onClose={() => setOpen(null)}
      />
    );

  return { actionsFor, dialog };
}
